// facilityResolver — "WHICH facility is this device standing at?"
//
// ─────────────────────────────────────────────────────────────────────────────
// WHY THIS IS NOT `checkGeofence`
// ─────────────────────────────────────────────────────────────────────────────
// `checkGeofence(facilityId, ...)` in providerMap.js VERIFIES: the facility is
// an INPUT, and the question is "is this device at X?". It answers
// `{ ok: true, reason: "no-geofence-configured" }` when there is nothing to
// check, and GeofenceGate then renders a warning and LOADS THE BOARD ANYWAY —
// asserted deliberately in GeofenceGate.test.js so a fresh tenant is not locked
// out of its own board on day one. That fail-OPEN posture is correct for a
// personal device carrying an account that already knows its facility.
//
// This function RESOLVES: the facility is the OUTPUT. Failing open is not
// merely wrong here, it is incoherent — you cannot resolve to nothing and then
// proceed. A wall display that silently renders another hospital's OR board is
// a tenancy and patient-safety failure; a wall display that asks which hospital
// it is in is an inconvenience. So every uncertain case fails CLOSED to
// `none`/`ambiguous`, and the caller shows a picker.
//
// Three things it deliberately does NOT inherit from providerMap:
//
//   1. FACILITY_PINS. Those are this deployment's AdventHealth campuses. A
//      resolver falling back to them would place a device standing in an
//      unrelated hospital in Tampa at "AdventHealth Tampa". Doc fences only.
//   2. The 1500 m default radius. `parseGeofence` substitutes it when
//      `radiusMeters` is absent; a resolver must not invent a catchment area it
//      was never told about, so an absent radius SKIPS the fence.
//   3. Any regional bounding box (cf. the Tampa box in setupPage.map.js). ATC
//      is meant to run at hospitals outside this deployment; validation here is
//      tenant-neutral or it is debt.
//
// What it DOES reuse: `distanceMeters` (already NaN-safe against the
// `Number(null) === 0` trap) and `parseGeofence`'s coordinate validation.
//
// ─────────────────────────────────────────────────────────────────────────────
// THE (0, 0) CASE
// ─────────────────────────────────────────────────────────────────────────────
// `Number(null)`, `Number("")` and `Number([])` are all 0, and 0 is a valid
// latitude and a valid longitude — (0, 0) is open water in the Gulf of Guinea.
// `parseGeofence` warns about this in its header but its own guard passes
// (0, 0), because for a VERIFIER the consequence is benign by accident: every
// real device is far more than any sane radius from the Atlantic, so the check
// fails closed on its own.
//
// For a RESOLVER it is not benign — a facility whose fence lost a coordinate
// would sit at (0, 0) and could match a device whose position ALSO collapsed to
// (0, 0), resolving confidently to the wrong hospital. So (0, 0) is rejected
// explicitly, on both sides. That one guard is the reason this is a new module
// rather than a flag on an old one.
//
// Pure. No React, no Firestore, no `navigator`.

import { distanceMeters, parseGeofence } from "./providerMap";

// A position is usable only if it is finite, in range, and not the coerced-null
// signature. No "close enough" — an unusable fix resolves to `none`.
export function isUsablePosition(lat, lng) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat < -90 || lat > 90) return false;
  if (lng < -180 || lng > 180) return false;
  if (lat === 0 && lng === 0) return false;
  return true;
}

// A fence is usable only if its coordinates validate, its radius was EXPLICITLY
// stored (never defaulted), and it is not at (0, 0).
//
// Returns { lat, lng, radiusM } or null. Null means "skip this facility" — it
// never means "match it anyway" and never means "use a default radius".
export function usableFence(facility) {
  if (!facility || typeof facility !== "object") return null;
  if (facility.isActive === false) return null;

  const raw = facility.geofence;
  // parseGeofence rejects bad coordinates, and rejects a radius that is stored
  // but nonsense (0, negative, unparseable). It does NOT reject an absent
  // radius — it substitutes the deployment default, which is exactly what must
  // not happen here, so the explicit-storage check below stands on top of it.
  const parsed = parseGeofence(raw);
  if (!parsed) return null;

  const stored = raw.radiusMeters;
  const radiusStored = stored !== undefined && stored !== null && stored !== "";
  if (!radiusStored) return null;

  if (parsed.lat === 0 && parsed.lng === 0) return null;

  return { lat: parsed.lat, lng: parsed.lng, radiusM: parsed.radiusMeters };
}

function facilityLabel(facility, facilityId) {
  const name = facility && (facility.name || facility.label);
  return typeof name === "string" && name.trim() !== "" ? name : facilityId;
}

function facilityKey(facility) {
  const id = facility && (facility.id || facility.facilityId);
  return typeof id === "string" && id.trim() !== "" ? id : null;
}

// Normalise the input list once: array-only, id required, first occurrence of a
// duplicated id wins. A facility appearing twice is a caller bug, not an
// ambiguity — resolving it to "ambiguous" would send a correctly-configured
// wall to the picker forever.
function normaliseFacilities(facilities) {
  if (!Array.isArray(facilities)) return [];
  const seen = new Set();
  const out = [];
  for (const f of facilities) {
    const id = facilityKey(f);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, facility: f });
  }
  return out;
}

function candidateFor(id, facility, lat, lng) {
  const fence = usableFence(facility);
  if (!fence) {
    return {
      facilityId: id,
      label: facilityLabel(facility, id),
      distanceM: null,
      radiusM: null,
      inRange: false,
    };
  }
  const d = distanceMeters(lat, lng, fence.lat, fence.lng);
  const distanceM = Number.isFinite(d) ? d : null;
  return {
    facilityId: id,
    label: facilityLabel(facility, id),
    distanceM,
    radiusM: fence.radiusM,
    // Inclusive, matching checkGeofence's `<=`, so the two functions can never
    // disagree about a device standing exactly on the line.
    inRange: distanceM !== null && distanceM <= fence.radiusM,
  };
}

// Every facility, with its distance from the device — for the picker's badges.
//
// With NO usable fix this returns [] rather than a list of nulls: a picker
// showing "—" against every row invites the reader to believe a distance was
// measured and came back empty, when in fact nothing was ever measured.
// Facilities WITHOUT a usable fence still appear (the human may still need to
// pick them) but carry `distanceM: null`.
//
// Ordering: in-range first, then by ascending distance, then unmeasurable, so
// the likeliest choice is nearest the top without the list ever claiming to
// have decided.
export function facilityDistances(lat, lng, facilities) {
  if (!isUsablePosition(lat, lng)) return [];
  const rows = normaliseFacilities(facilities).map(({ id, facility }) =>
    candidateFor(id, facility, lat, lng)
  );
  return rows.sort((a, b) => {
    if (a.inRange !== b.inRange) return a.inRange ? -1 : 1;
    const ad = a.distanceM, bd = b.distanceM;
    if (ad === null && bd === null) return 0;
    if (ad === null) return 1;
    if (bd === null) return -1;
    return ad - bd;
  });
}

// Resolve the device's facility from its position.
//
//   { status: "resolved",  facilityId, distanceM, radiusM }
//   { status: "ambiguous", candidates: Candidate[] }   — 2+ fences contain it
//   { status: "none",      candidates: [] }            — nothing does, or input
//                                                        was unusable
//
// A tagged union rather than `facilityId | null` so the caller can label WHY it
// is showing a picker without re-deriving the reason.
//
// TWO OR MORE MATCHES IS ALWAYS `ambiguous`. The tie is never broken by
// distance. Overlapping campus fences are a configuration error, and the right
// response to a configuration error on a safety surface is to ask a human —
// silently preferring the nearer one would make a misconfigured tenant look
// like a working one right up until the day it matters.
export function resolveFacilityByPosition(lat, lng, facilities) {
  if (!isUsablePosition(lat, lng)) return { status: "none", candidates: [] };

  const matches = [];
  for (const { id, facility } of normaliseFacilities(facilities)) {
    const fence = usableFence(facility);
    if (!fence) continue;
    const d = distanceMeters(lat, lng, fence.lat, fence.lng);
    if (!Number.isFinite(d)) continue;
    if (d <= fence.radiusM) {
      matches.push({
        facilityId: id,
        label: facilityLabel(facility, id),
        distanceM: d,
        radiusM: fence.radiusM,
        inRange: true,
      });
    }
  }

  if (matches.length === 1) {
    const m = matches[0];
    return {
      status: "resolved",
      facilityId: m.facilityId,
      distanceM: m.distanceM,
      radiusM: m.radiusM,
    };
  }
  if (matches.length > 1) {
    return {
      status: "ambiguous",
      candidates: matches.sort((a, b) => a.distanceM - b.distanceM),
    };
  }
  return { status: "none", candidates: [] };
}
